/**
 * Viewer navigation modes (card t_9cf7e787; FLOAT retired by card t_493c5f58).
 *
 * The pure half of the trio — no three.js, no DOM beyond the storage accessors
 * the composable hands in — so the mode rules, the look maths, the movement
 * integration and the floor model are all unit-testable in Node:
 *
 *   · ORBIT    — the turntable the viewer has always been (untouched);
 *   · GRAVITY  — walking: horizontal movement at eye height, the camera's Y
 *                clamped to the model's own walking surfaces (one step at a
 *                time, so stairs work) and never a hardcoded floor;
 *   · SIMULATION     — the same walk with the camera off the body (card t_a41a75f6):
 *                the driven avatar is the movement point and the camera chases
 *                it, which is what makes an avatar worth driving at all.
 *
 * The owner, 2026-10-12, verbatim: *"we should remove the float mode.."* — the
 * fourth mode (free flight: WASD along the view, Q/E down/up, no gravity) is
 * gone: its mode entry, its keys, its vertical axis and its speed constant with
 * it, and a stored `float` reads as ORBIT (the strict reader below), so a
 * session that was in flight lands on the turntable rather than on a mode that
 * no longer exists.
 *
 * The walking surface is derived from the loaded model, never guessed: the
 * floor-like finishes (`COVERING`, the rooms' own "… Floor Finish") and the
 * storey elevations give the level list, and the live surface underfoot comes
 * from the scene's own raycast (see `useBimScene`'s walk probe).
 */

export type BimNavMode = 'orbit' | 'gravity' | 'sim';

/** The trio in toolbar order — orbit first, because it is the default. */
export const BIM_NAV_MODES: readonly BimNavMode[] = ['orbit', 'gravity', 'sim'];

/** Does this value name a mode? A stored or network value is checked, never trusted. */
export function isBimNavMode(value: unknown): value is BimNavMode {
  return typeof value === 'string' && (BIM_NAV_MODES as readonly string[]).includes(value);
}

/** Per-user persistence of the active mode (local state is enough — the card). */
export const BIM_NAV_STORAGE_KEY = 'bim-nav-mode';

/**
 * An unreadable/unknown stored value is orbit, never a guess. The mode's own id
 * was `game` until card t_50ce0e5d renamed it (`Simulation`): a session that
 * stored the old spelling reads as SIMULATION — the mode did not change, only
 * its name did, and dropping those sessions to orbit would look like a bug.
 */
export function parseNavMode(raw: unknown): BimNavMode {
  if (raw === 'game') return 'sim';
  return raw === 'gravity' || raw === 'sim' || raw === 'orbit' ? raw : 'orbit';
}

export function readStoredNavMode(storage: Pick<Storage, 'getItem'> | null | undefined): BimNavMode {
  if (!storage) return 'orbit';
  try {
    return parseNavMode(storage.getItem(BIM_NAV_STORAGE_KEY));
  } catch {
    return 'orbit';
  }
}

export function storeNavMode(mode: BimNavMode, storage: Pick<Storage, 'setItem'> | null | undefined): void {
  if (!storage) return;
  try {
    storage.setItem(BIM_NAV_STORAGE_KEY, mode);
  } catch {
    /* private mode / quota — the mode still applies for this session */
  }
}

export function isFirstPersonMode(mode: BimNavMode): boolean {
  return mode === 'gravity' || mode === 'sim';
}

/**
 * The modes that WALK: the camera is on the ground and the floor probe decides
 * the height (card t_a41a75f6's SIMULATION mode walks too — it is GRAVITY with the
 * camera lifted off the body, not a second kind of movement). Since FLOAT's
 * retirement (card t_493c5f58) these are also the only first-person modes, but
 * the two predicates stay separate: "the camera is the character" and "the floor
 * carries it" are still two different rules.
 */
export function isWalkerMode(mode: BimNavMode): boolean {
  return mode === 'gravity' || mode === 'sim';
}

/** The mode where the camera watches the driven avatar instead of being it. */
export function isThirdPersonMode(mode: BimNavMode): boolean {
  return mode === 'sim';
}

/**
 * The distances SIMULATION's camera works in (card t_a41a75f6). The owner's ask was to
 * *"control the movement of the user via keys"* while still seeing the person
 * they drive, so SIMULATION grew a camera that watches a separate body — the drone over
 * its head since 2026-10-13.
 *
 * The pivot is the body's own aim point (half a metre over its head in the drone
 * view); `SIM_CAM_DISTANCE_M` is how far the drone stands from it, which the
 * wheel changes and the distance itself never bends for a wall.
 */
export const SIM_CAM_DISTANCE_M = 4.4;
/**
 * The pivot's height over the driven avatar's feet (a person's chest/head).
 * Halved with the body itself (card t_565b83bf: *"the user avatars are much too
 * big. reduce to 50%"*): at 0.58 m the drone aims at the 0.85 m figure it
 * follows, where the original 1.15 m aimed at the air a full-size person's head
 * would have been in.
 */
export const SIM_CAM_EYE_M = 0.58;
/** Never closer than this (metres). */
export const SIM_CAM_MIN_DISTANCE_M = 0.9;

/**
 * How far the drone may stand from the body at MOST (metres; owner, 2026-10-14:
 * *"mouse wheel = camera distance from character.. in simulate view"*). The
 * shared wheel ceiling (`SIM_ZOOM_MAX_M`, a turntable's zoom) is no ceiling for a
 * camera that watches a body: past this the figure is a speck and the section cut
 * shows a slice of roof. 14 m still frames the body and the room around it.
 */
export const SIM_DRONE_MAX_DISTANCE_M = 14;

/**
 * The drone's own distance band, applied at the writer (the wheel) and again
 * inside `droneCameraPose` — so the promise the view is built on (the camera is
 * exactly this far from the body) cannot be broken by a number that reached the
 * state from anywhere else.
 */
export function clampDroneDistance(distance: number): number {
  if (!Number.isFinite(distance)) return SIM_CAM_DISTANCE_M;
  return Math.min(SIM_DRONE_MAX_DISTANCE_M, Math.max(SIM_CAM_MIN_DISTANCE_M, distance));
}

/** A pose of a camera that watches the driven body rather than being it. */
export interface SimCameraPose {
  /** Where the camera goes. */
  position: NavPoint;
  /** What it looks at (the avatar's head). */
  target: NavPoint;
}

/** Standing eye height over the walking surface (metres). */
export const NAV_EYE_HEIGHT_M = 1.65;

// ─── SIMULATION's DRONE view (owner, 2026-10-13; ceiling cap 2026-10-19) ────────────

/**
 * How far above the cut plane the drone's camera stands (owner's own view).
 */
export const DRONE_CUT_CLEARANCE_M = 0.35;

/** What the drone looks at: the body itself, so the character stays framed. */
export function droneAimPoint(feet: NavPoint): NavPoint {
  return { x: feet.x, y: feet.y + SIM_CAM_EYE_M, z: feet.z };
}

/**
 * One frame of the drone camera (owner, 2026-10-13; the FOLLOW rule 2026-10-14).
 *
 * The camera is a drone: it keeps a constant distance from the body and always
 * looks DOWN at it (the pitch clamped to a downward band, never below the body's
 * own aim). The angles it is handed are where it really stands: `droneCameraLook`
 * is what derives them — the BODY's heading plus the manual orbit offset, so the
 * camera stays behind whoever the view follows.
 *
 * The vertical rule the owner means is NOT the camera — it is the SECTION CUT this
 * view drives, and since 2026-10-19 that plane is the CEILING of the storey the
 * body stands in (owner: *"we need to cap the ceiling … remove cut following the
 * user head height.. cap to the floor levels"*), handed in as `cutY`: the storey
 * above and the roof come off, and the room he is in stays whole. `null` — no
 * storey found, i.e. outside the house — means no cut, so the camera is free.
 *
 * The distance is the wheel's own number, in the drone's band (`clampDroneDistance`).
 */
export function droneCameraPose(
  feet: NavPoint,
  look: NavLook,
  distance: number = SIM_CAM_DISTANCE_M,
  cutY: number | null = null,
): SimCameraPose {
  const target = droneAimPoint(feet);
  const reach = clampDroneDistance(distance);
  // The distance is the promise that must not bend, so the CUT is cleared by the
  // look instead: the pitch steepens just enough that the camera — `reach` away on
  // that line — stands above the section plane. A camera under its own cut would
  // look across it instead of down through it, which is the one thing this view is
  // for (owner, 2026-10-13: *"to see the action from above"*).
  const clearance = cutY !== null && Number.isFinite(cutY) ? cutY + DRONE_CUT_CLEARANCE_M - target.y : 0;
  const needSteep = clearance > 0 ? Math.asin(Math.min(0.995, Math.max(0, clearance / reach))) : 0;
  const asked = Math.max((-DRONE_PITCH_MAX_DEG * Math.PI) / 180, look.pitch);
  const pitch = Math.min(Math.min((-DRONE_PITCH_MIN_DEG * Math.PI) / 180, asked), -needSteep);
  const forward = directionFromLook({ yaw: look.yaw, pitch });
  return {
    position: {
      x: target.x - forward.x * reach,
      y: target.y - forward.y * reach,
      z: target.z - forward.z * reach,
    },
    target,
  };
}

/** How far below the horizon the drone's look may start, in degrees. */
export const DRONE_PITCH_DEG = 36;
export const DRONE_PITCH_MIN_DEG = 20;
export const DRONE_PITCH_MAX_DEG = 80;

/**
 * The drone's own steering (owner, 2026-10-13: *"we cloud lik wasd keys to camera
 * control.. in drone mode"*, chosen as "orbit/steer the camera around the
 * character (A/D orbit, W/S raise-lower), keeping the constant distance").
 */
export interface DroneSteer {
  /** +1 orbits the camera right around the body, -1 left (A/D). */
  orbit: number;
  /** +1 lifts it toward overhead, -1 brings it down (W/S). */
  lift: number;
}

/**
 * The four LETTERS the drone view takes for its own steering. They are the only
 * keys it takes: the arrow keys keep the character (see `withoutDroneLetters`),
 * so the drone can fly around a body that walks under it — which is what the
 * view exists for (*"to see the action from above"*).
 */
export const DRONE_STEER_LETTERS: readonly string[] = ['w', 'a', 's', 'd'];

/** One frame of drone steering, read off the letters alone. */
export function droneSteerFromPressed(pressed: ReadonlySet<string>): DroneSteer {
  const down = (key: string) => (pressed.has(key) ? 1 : 0);
  return { orbit: down('d') - down('a'), lift: down('w') - down('s') };
}

/** The same keys, with the drone's own letters taken out — what walks the body. */
export function withoutDroneLetters(pressed: ReadonlySet<string>): ReadonlySet<string> {
  const rest = new Set(pressed);
  for (const key of DRONE_STEER_LETTERS) rest.delete(key);
  return rest;
}

/**
 * The drone's own ZOOM keys (owner, 2026-10-15: *"simulation drone view +, -,
 * shuld change the disctane of the camera (zoom).."*): `+` (and the bare `=` on
 * its key, as the turntable's keyboard reads it) asks for CLOSER, `-` for FARTHER.
 * They are read exactly as the letters are — one held-key number per frame — so a
 * tap is a nudge and a hold is a smooth run, and holding both cancels as holding
 * W and S does.
 */
export function droneZoomFromPressed(pressed: ReadonlySet<string>): number {
  const closer = pressed.has('+') || pressed.has('=');
  return (closer ? 1 : 0) - (pressed.has('-') ? 1 : 0);
}

/** How fast the drone orbits the body (A/D) and climbs or drops (W/S). */
export const DRONE_ORBIT_RATE_RAD_S = 1.1;
export const DRONE_LIFT_RATE_RAD_S = 0.9;

/** The drone's pitch band: always looking down, between its own two limits. */
export function clampDronePitch(pitch: number): number {
  const ceiling = (-DRONE_PITCH_MIN_DEG * Math.PI) / 180;
  const floor = (-DRONE_PITCH_MAX_DEG * Math.PI) / 180;
  return Math.min(ceiling, Math.max(floor, pitch));
}

/**
 * One frame of the drone's ORBIT — the A/D letters (owner, 2026-10-14:
 * *"make drone view camera stay behind the person"*, with *"wasd in drone view is
 * now working ok"* meaning the sense must not move under him).
 *
 * What the letters move is no longer the camera's own yaw: the camera's yaw IS
 * the body's heading now (`droneCameraLook`), and A/D add this OFFSET around it,
 * so the same key still turns the view the same way (`d` right, exactly as the
 * A/D pair turns a character) and the drone can still be swung aside to look at
 * the body from an angle. The offset is not the drag's business: a hand on the
 * pointer turns the CHARACTER, a hand on the keys swings the camera around it.
 */
export function applyDroneOrbit(orbitOffset: number, steer: DroneSteer, dtSeconds: number): number {
  const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
  const from = Number.isFinite(orbitOffset) ? orbitOffset : 0;
  return wrapRadians(from - steer.orbit * DRONE_ORBIT_RATE_RAD_S * dt);
}

/** …and one frame of its LIFT: W/S move the look's pitch inside the drone's band. */
export function applyDroneLift(look: NavLook, steer: DroneSteer, dtSeconds: number): NavLook {
  const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
  return { yaw: look.yaw, pitch: clampDronePitch(look.pitch - steer.lift * DRONE_LIFT_RATE_RAD_S * dt) };
}

/**
 * How fast the manual orbit eases back to directly behind while the CHARACTER
 * WALKS (1/s; owner, 2026-10-14: a follow camera that recovers behind you), and
 * the deadband under which the tail is simply zero.
 *
 * Standing still the offset HOLDS where the user put it — a camera that eased
 * back on its own would undo the A/D keys the moment the hand left them.
 */
export const DRONE_RECENTER_RATE_S = 2.2;
export const DRONE_RECENTER_DEADBAND_DEG = 1;

/** One frame of the follow rule: ease behind while walking, hold while standing. */
export function easeDroneOrbitOffset(orbitOffset: number, walking: boolean, dtSeconds: number): number {
  if (!Number.isFinite(orbitOffset)) return 0;
  if (!walking) return orbitOffset;
  const next = approach(orbitOffset, 0, DRONE_RECENTER_RATE_S, dtSeconds);
  const deadband = (DRONE_RECENTER_DEADBAND_DEG * Math.PI) / 180;
  return Math.abs(next) <= deadband ? 0 : next;
}

/**
 * The drone's DRAG, sent the other way round (owner, 2026-10-15: *"in sim mode
 * the mouse drags should be inverted in direction.."* — and, asked which view,
 * *"sim mode. drone view!"*).
 *
 * Every other drag in the viewer pulls the WORLD (`applyLookDelta`,
 * `turnYawDegByDrag`): drag right and what is being watched goes left. This view
 * is the exception the owner named — drag right and the character turns right,
 * drag up and the view tilts up, the camera coming down off its overhead angle
 * toward the body's own level.
 *
 * It is ONE mirror of the pointer's two deltas and nothing else. The axis
 * OWNERSHIP is unchanged (the horizontal turns the character, the vertical is the
 * camera's own angle, in `clampDronePitch`'s band), and the drone's own keys keep
 * their sense (`d` right and `w` toward overhead): a hand on the pointer and a
 * hand on the keys are allowed to differ, as they do everywhere else here.
 */
export function droneDragDeltas(dxPx: number, dyPx: number): { dx: number; dy: number } {
  const mirrored = (px: number) => (Number.isFinite(px) && px !== 0 ? -px : 0);
  return { dx: mirrored(dxPx), dy: mirrored(dyPx) };
}

/** A bearing folded into [0, 360). */
export function normalizeYawDeg(yawDeg: number): number {
  if (!Number.isFinite(yawDeg)) return 0;
  return ((yawDeg % 360) + 360) % 360;
}

/** An angle folded into a single turn, [−π, π). */
export function wrapRadians(angle: number): number {
  if (!Number.isFinite(angle)) return 0;
  const folded = (((angle + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return folded - Math.PI;
}

/**
 * The body's own heading as a look yaw (radians) — three's convention, where a
 * yaw of 0 faces −Z. The avatar layer draws the body at this bearing and the
 * camera hangs off it, so the two must use one spelling.
 */
export function bodyHeadingYaw(yawDeg: number): number {
  return (normalizeYawDeg(yawDeg) * Math.PI) / 180;
}

/** The inverse of `bodyHeadingYaw`: the bearing a look yaw means, in degrees. */
export function yawDegFromLook(yaw: number): number {
  return normalizeYawDeg((yaw * 180) / Math.PI);
}

/**
 * The look the drone hangs on (owner, 2026-10-14: *"make drone view camera stay
 * behind the person"*): the camera stands BEHIND the body's own heading, swung by
 * the manual orbit offset, and looks down at the body on its own pitch. Turning
 * the character — with the arrows, or with the drag below — therefore swings the
 * camera with it, and the body is what the view is always of.
 */
export function droneCameraLook(bodyYawDeg: number, orbitOffset: number, pitch: number): NavLook {
  return {
    yaw: bodyHeadingYaw(bodyYawDeg) + (Number.isFinite(orbitOffset) ? orbitOffset : 0),
    pitch,
  };
}

/**
 * The character's own bearing after a HORIZONTAL drag (px) in the drone view
 * (owner, 2026-10-14: *"mouse drag can … turn the character (left/right)"*).
 *
 * The sign is `applyLookDelta`'s — this app's ONE drag convention, the owner's own
 * correction of 2026-10-12 (*"left nad right is off"*): a drag takes hold of the
 * WORLD and pulls it, so a drag to the right turns what is being watched to the
 * LEFT. The gesture is the same hand movement everywhere in the viewer, so it
 * keeps the same sense; what differs here is only what it turns — the character.
 * (The SIMULATION drone is the one view the owner asked to mirror: it sends its
 * deltas through `droneDragDeltas` first.)
 */
export function turnYawDegByDrag(yawDeg: number, dxPx: number, sensitivity: number = NAV_LOOK_SENSITIVITY): number {
  const deltaDeg = ((Number.isFinite(dxPx) ? dxPx : 0) * sensitivity * 180) / Math.PI;
  return normalizeYawDeg(yawDeg + deltaDeg);
}

/**
 * One frame of the CHARACTER's own turning (degrees), for the arrow keys while
 * the drone view is on: the same rate and the same sense the steering keys carry
 * in POV, applied to the body instead of to the camera — the drone's own angle is
 * the FOLLOW rule's (behind the body) plus the letters' orbit offset.
 */
export function turnYawDeg(
  yawDeg: number,
  turn: number,
  dtSeconds: number,
  rateRad: number = NAV_TURN_RATE_RAD_S,
): number {
  const next = yawDeg - (turn * rateRad * dtSeconds * 180) / Math.PI;
  return normalizeYawDeg(next);
}

// ─── The wheel is the zoom (owner, 2026-10-13) ────────────────────────────────

/**
 * The owner: *"remove lock here for each mode you must drag the view. zoom with
 * scoll"* — and with the pointer no longer locked, the wheel has to mean zoom in
 * every first-person mode. In the drone view it is the DISTANCE from the body;
 * through the character's own eyes it is the camera's field of view.
 */
export const SIM_ZOOM_MIN_M = 1.6;
export const SIM_ZOOM_MAX_M = 26;

/** One wheel notch, as a factor. A bigger `deltaY` (a trackpad fling) zooms more. */
export function zoomDistanceByWheel(current: number, deltaY: number): number {
  const turns = Math.min(3, Math.max(1, Math.abs(deltaY) / 100));
  const factor = Math.pow(1.12, deltaY > 0 ? turns : -turns);
  const next = current * factor;
  return Math.min(SIM_ZOOM_MAX_M, Math.max(SIM_ZOOM_MIN_M, next));
}

/**
 * …and the wheel on the DRONE's own distance (owner, 2026-10-14: *"mouse wheel =
 * camera distance from character"*): the shared zoom, then the drone's band
 * (`clampDroneDistance` — never closer than its floor, never past
 * `SIM_DRONE_MAX_DISTANCE_M`). This is the ONE writer of the distance, so the
 * number the state holds is always the number the camera stands at.
 */
export function zoomDroneDistanceByWheel(current: number, deltaY: number): number {
  return clampDroneDistance(zoomDistanceByWheel(clampDroneDistance(current), deltaY));
}

/**
 * How fast a HELD zoom key moves the drone's distance, as a fraction per second
 * (owner, 2026-10-15: *"simulation drone view +, -, shuld change the disctane of
 * the camera (zoom).."*). 1.2/s crosses the drone's whole band in about a second
 * and a half, and a tap is a step of a tenth the distance — the wheel's own grain,
 * in the keyboard's smooth form.
 */
export const DRONE_KEY_ZOOM_RATE_S = 1.2;

/**
 * …and one frame of the ZOOM KEYS on the same number, beside the wheel: `zoom` is
 * `droneZoomFromPressed`'s own (+1 closer, −1 farther), the move is
 * MULTIPLICATIVE exactly as the wheel's is — a step is the same fraction of the
 * distance wherever the camera stands — and it ends in the SAME two clamps the
 * wheel ends in (`zoomDistanceByWheel`'s shared band, then the drone's own), so
 * pressing `+` all the way in leaves the standoff the wheel leaves: one band,
 * whichever hand moves the number.
 */
export function zoomDroneDistanceByKeys(current: number, zoom: number, dtSeconds: number): number {
  const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
  const step = Number.isFinite(zoom) ? Math.sign(zoom) : 0;
  if (step === 0 || dt === 0) return clampDroneDistance(current);
  const next = clampDroneDistance(current) * Math.exp(-step * DRONE_KEY_ZOOM_RATE_S * dt);
  return clampDroneDistance(Math.min(SIM_ZOOM_MAX_M, Math.max(SIM_ZOOM_MIN_M, next)));
}

/** The POV's zoom is its own lens: wheel-down widens the view, wheel-up narrows it. */
export const POV_FOV_MIN_DEG = 34;
export const POV_FOV_MAX_DEG = 78;

export function fovByWheel(current: number, deltaY: number): number {
  const step = deltaY > 0 ? 3 : -3;
  return Math.min(POV_FOV_MAX_DEG, Math.max(POV_FOV_MIN_DEG, current + step));
}

/**
 * The wheel WALKS in GRAVITY (owner, 2026-10-14: *"scroll to zoom should be
 * disbaled in walko mode.. scroll should move the camera forwaerd, backward.. zoom
 * should be minimal"*).
 *
 * It returns the FORWARD impulse one notch asks for, in the same −1…1 the letter
 * keys write, so the wheel is a second way to press W rather than a second kind of
 * movement: the walk's own speed, velocity smoothing, floor probe and blocked-step
 * rule all still apply, and nothing here moves a camera itself.
 *
 * Scroll DOWN walks BACKWARD — this app's wheel already reads down as "away"
 * (`zoomDistanceByWheel` zooms out on a down-tick), and the walk keeps that reading.
 *
 * Several notches in a row SATURATE at 1 (full walking speed) instead of stacking
 * past it; the caller bleeds the impulse off with `approach` at
 * `NAV_WHEEL_WALK_DECAY_PER_S`, so one notch is a step and a scroll is a walk.
 *
 * HOW FAR ONE NOTCH WALKS is these two constants together with the walk's own
 * speed: roughly `NAV_WALK_SPEED_MS × NAV_WHEEL_WALK_STEP / NAV_WHEEL_WALK_DECAY_PER_S`
 * — about 8 cm at the values below. The owner, 2026-10-14: *"but on scroll its
 * moving to much.."* — THIS is the knob. Lower the step (or raise the decay) for a
 * gentler wheel, and note the two compound: the first version was 0.5 at 6, which
 * walked nearly three times as far per notch.
 */
export const NAV_WHEEL_WALK_STEP = 0.25;

/** How fast the wheel's impulse bleeds off once the wheel stops (1/s); see the step above. */
export const NAV_WHEEL_WALK_DECAY_PER_S = 8;

export function wheelWalkImpulse(current: number, deltaY: number): number {
  const step = deltaY > 0 ? -NAV_WHEEL_WALK_STEP : NAV_WHEEL_WALK_STEP;
  return Math.min(1, Math.max(-1, current + step));
}

// ─── SIMULATION mode's two camera views (card t_01ebef40) ───────────────────────────

/**
 * The owner, 2026-10-11: *"in game mode we should have two views: camera behind
 * the model and pov view.."* — SIMULATION grows a view switch. The "camera behind the
 * model" view has been the DRONE since 2026-10-13 (the chase camera under it was
 * retired: *"think we should remove chase mode .. have only drone mode"*).
 *
 * The MOVEMENT belongs to the character in POV and to the CAMERA in the drone
 * view (owner, 2026-10-13: *"in drone mode we should have mouse move the angle of
 * the camera"*, *"we cloud lik wasd keys to camera control.. in drone mode"*):
 * what changes between the two is where the camera sits, whether the body is
 * drawn, and whose keys are whose.
 *
 *   · DRONE — behind and above the character, looking down: it hangs a constant
 *     distance from a point 50 cm over their head, stays BEHIND the body's own
 *     heading (owner, 2026-10-14: *"make drone view camera stay behind the
 *     person"* — turning the character swings the camera with it, and the manual
 *     A/D orbit is an offset around that heading which eases back behind while
 *     the character walks), and the section cut is driven to that same plane so
 *     the floor around them is visible. The MOUSE owns the character and the
 *     camera's angle: a horizontal drag turns the character, a vertical drag
 *     lifts and lowers the camera's look, and the wheel is the distance from the
 *     body (min `SIM_CAM_MIN_DISTANCE_M`, max `SIM_DRONE_MAX_DISTANCE_M`). The
 *     LETTERS are the camera's (A/D orbit the offset, W/S raise and lower), the
 *     ARROWS are the character's. It is the DEFAULT: an existing session opens in
 *     the view that watches the body.
 *   · POV   — through the character's own eyes: the camera leaves the drone
 *     placement and stands at the body's head (the same pose GRAVITY walks
 *     with), the driven body itself is not drawn — you cannot see your own head,
 *     and a picture billboard drawn at the camera's own position would fill the
 *     whole screen — and the movement keys are the character's again.
 */
export type BimSimView = 'pov' | 'drone';
export const BIM_SIM_VIEWS: readonly BimSimView[] = ['pov', 'drone'];

/** Does this value name a simulation view? */
export function isBimSimView(value: unknown): value is BimSimView {
  return typeof value === 'string' && (BIM_SIM_VIEWS as readonly string[]).includes(value);
}

/** Per-user persistence of SIMULATION's camera view — same rule as the mode itself. */
export const BIM_SIM_VIEW_STORAGE_KEY = 'bim-sim-view';

/** The key the view was stored under while the mode was still called `game`. */
export const BIM_SIM_VIEW_LEGACY_STORAGE_KEY = 'bim-game-view';

/**
 * An unreadable/unknown stored value is the drone — the view that watches the
 * body. A stored `chase`, from the builds that still had one, reads as the drone
 * that replaced it (its own camera: the chase placement is gone).
 */
export function parseSimView(raw: unknown): BimSimView {
  if (raw === 'pov' || raw === 'drone') return raw;
  return 'drone';
}

/**
 * The stored view, under the current key — or, for a session that stored it
 * before the rename, under the legacy one (card t_50ce0e5d: the mode kept its
 * meaning, so the view the user picked must survive the name change).
 */
export function readStoredSimView(storage: Pick<Storage, 'getItem'> | null | undefined): BimSimView {
  if (!storage) return 'drone';
  try {
    return parseSimView(storage.getItem(BIM_SIM_VIEW_STORAGE_KEY) ?? storage.getItem(BIM_SIM_VIEW_LEGACY_STORAGE_KEY));
  } catch {
    return 'drone';
  }
}

export function storeSimView(view: BimSimView, storage: Pick<Storage, 'setItem'> | null | undefined): void {
  if (!storage) return;
  try {
    storage.setItem(BIM_SIM_VIEW_STORAGE_KEY, view);
  } catch {
    /* private mode / quota — the view still applies for this session */
  }
}

/**
 * The other view — what the `C` key and the pill's chip apply. Two views is a
 * TOGGLE, not a cycle: there is nothing else to walk through.
 */
export function oppositeSimView(view: BimSimView): BimSimView {
  return view === 'drone' ? 'pov' : 'drone';
}

/**
 * The look a view OPENS with (owner, 2026-10-13; POV levels, 2026-10-20): the drone
 * starts steeply above the body — its own default steepness — so entering the game,
 * switching the view or handing the game to another character always starts the
 * same way, instead of inheriting whatever pitch the camera happened to arrive
 * with. POV keeps the FACING and looks at the horizon (owner, 2026-10-20: *"entering
 * simulate pov view should level the head"*): the steep pitch belongs to the drone,
 * and arriving at the body's own eyes must not land on the floor it was looking at.
 */
export function simViewOpeningLook(view: BimSimView, look: NavLook): NavLook {
  if (view !== 'drone') return { yaw: look.yaw, pitch: 0 };
  return { yaw: look.yaw, pitch: (-DRONE_PITCH_DEG * Math.PI) / 180 };
}

/**
 * Whether the camera IS the character's own eyes this frame (card t_01ebef40).
 * GRAVITY always is — the camera has always been the character there — and SIMULATION
 * is, in its POV view; the drone hangs the camera over the body instead.
 *
 * This is the rule the scene reads to decide between `applyLookRotation` (the
 * camera takes the look angles itself) and the drone placement, and the layout
 * reads to know whether the driven body is drawn.
 */
export function isPovCamera(mode: BimNavMode, view: BimSimView): boolean {
  return isFirstPersonMode(mode) && !(mode === 'sim' && view === 'drone');
}

/** True in SIMULATION's drone view: the one view whose keys steer the camera. */
export function isDroneView(mode: BimNavMode, view: BimSimView): boolean {
  return mode === 'sim' && view === 'drone';
}

/**
 * The POV pose of the driven body (card t_01ebef40): the camera stands at the
 * character's own eyes — the feet the floor probe resolved, plus the standing
 * eye height — and looks along the look angles. It is the same camera GRAVITY
 * walks with, which is the point of the view: it is the walk, seen from inside
 * the model the user drives.
 */
export function povCameraPose(feet: NavPoint, look: NavLook, eyeHeight: number = NAV_EYE_HEIGHT_M): SimCameraPose {
  const forward = directionFromLook(look);
  const position = { x: feet.x, y: feet.y + eyeHeight, z: feet.z };
  return {
    position,
    target: { x: position.x + forward.x, y: position.y + forward.y, z: position.z + forward.z },
  };
}

// ─── The POV camera's own eye (owner, 2026-10-20) ────────────────────────────
//
// *"the camera height in simulate pov view should be in the models eye level.. now
// its fixed?? calculate eye model height -5 %"* — the fixed standing height of
// 1.65 m ignored the body it was worn by (a driven body is not always human-sized),
// so the camera could park above or below its head. The eye is measured from the
// MODEL instead: its own measured height, 5 % under the top of the head.

/** How far under the model's top the eye sits (5 %). */
export const POV_EYE_FROM_MODEL_TOP = 0.95;

/**
 * The POV eye for a model of `modelHeight` metres — 5 % under its top. A model that
 * is not known yet (or a billboard, which has no body) falls back to the standing
 * height the walk uses.
 */
export function povEyeHeightForModel(modelHeight: number | null, fallback: number = NAV_EYE_HEIGHT_M): number {
  if (modelHeight === null || !Number.isFinite(modelHeight) || modelHeight <= 0.2) return fallback;
  return modelHeight * POV_EYE_FROM_MODEL_TOP;
}

/**
 * Whether a mesh is a WALL to the walker (card t_565b83bf). The owner,
 * 2026-10-11, verbatim: *"make wall block the movement in game mode.. so you
 * can't fall out.. we would allow the walking throught doors.."* — so the
 * building's envelope is what stops the walk: the walls (this house's facades,
 * `IFCWALL`/`IfcWallStandardCase`). The DOORS need no entry here: measured on
 * the served demo house, a door is not a mesh but a GAP — the wall solids carry
 * the boolean void, and 14 openings 0.8–1.6 m wide run through them
 * (`scripts/probe-wall-geometry-t_565b83bf.mjs`), so a probe that tests the
 * walls alone walks through a doorway and stops at a facade.
 *
 * WINDOWS pass too (owner, 2026-10-18: *"in simulate mode we should be blocked
 * by walls. can only go through doors and windows.."*). This rule used to add
 * the window glass to the blocking set; the served model is a GLB whose glazing
 * is part of the wall run, so blocking on `WINDOW` sealed openings the owner
 * walks through.
 *
 * Furniture, lamps and the shades do NOT block (noted in the handoff): a walker
 * stepping through the owner's own sofa is a smaller surprise than a walker
 * stuck on it, and the roller-blind meshes are dynamic cover state, not
 * building fabric.
 */
export function isWallMeshType(typeName: string): boolean {
  return typeName.toUpperCase().includes('WALL');
}

/**
 * The IFC object classes this app gives a behaviour to, in the spelling the IFC
 * path's `typeName` carries (web-ifc's own constant minus the `IFC` prefix —
 * `IFCWALLSTANDARDCASE` → `WALLSTANDARDCASE`).
 *
 * A glTF/GLB carries no element table, so the only place its types survive is
 * the node's own NAME, and a node name is spelled two ways depending on where
 * it is read: the file's `IfcWallStandardCase/Fasadni zid` (three's loader hands
 * it back unsanitized in `userData.name`) or three's sanitized `IfcWallStandardCaseFasadni_zid`
 * (`mesh.name`, which drops the separator). The list is the second spelling's
 * only way back to the class — the first one splits on the separator.
 */
export const IFC_OBJECT_CLASSES: readonly string[] = [
  'WALLSTANDARDCASE',
  'WALLELEMENTEDCASE',
  'WALL',
  'CURTAINWALL',
  'WINDOW',
  'DOORSTANDARDCASE',
  'DOOR',
  'SLABSTANDARDCASE',
  'SLABELEMENTEDCASE',
  'SLAB',
  'COVERING',
  'STAIRFLIGHT',
  'STAIR',
  'RAMPFLIGHT',
  'RAMP',
  'LANDING',
  'BUILDINGELEMENTPROXY',
  'BUILDINGELEMENTPART',
  'BUILDINGSTOREY',
  'BUILDING',
  'ROOF',
  'FURNITURE',
  'SPACE',
  'SITE',
];

/**
 * The element TYPE a loaded model's object name states, in the same spelling the
 * IFC path uses (`WALLSTANDARDCASE`, `COVERING`, `WINDOW`, …) — and the reason a
 * GLB gets the walk's own rules at all (2026-10-18).
 *
 * The served demo house is a GLB exported from IFC: its semantic table is gone,
 * but every node kept its class in its name (`IfcWallStandardCase/Fasadni zid
 * (pritličje).014`, `IfcBuildingElementProxy/TREAD-235`). Without this the walk
 * had **no walls that block** (the owner's *"in simulate mode we should be
 * blocked by walls"*) and **no stair in its walking surfaces** — a tread's box
 * is 0.31 × 1.1 m, too narrow for the plate rule, so the support probe under the
 * flight landed on the floor finish below it and the walker stayed at ground
 * level (the owner's *"gravity works, but you can not climb the stairs"*).
 *
 * `''` for anything that names no IFC class (a scanner's `Wall0`, an unnamed
 * node): the rules then fall back to what they had before, never to a guess.
 */
export function elementTypeNameFromObjectName(name: string): string {
  const raw = String(name ?? '').trim();
  if (raw === '') return '';
  const slash = raw.indexOf('/');
  if (slash > 0) {
    // The exporter's own spelling (`IfcWallStandardCase/Fasadni zid`): the class
    // is the whole head, and the element's own name follows the separator.
    const head = raw.slice(0, slash).toUpperCase();
    return /^IFC[A-Z0-9]+$/.test(head) ? head.slice(3) : '';
  }
  const upper = raw.toUpperCase();
  if (!upper.startsWith('IFC')) return '';
  const withoutPrefix = upper.slice(3);
  // Three's sanitized node name (`IfcBuildingElementProxyTREAD-235`): the class
  // is the longest one the name starts with.
  let best = '';
  for (const className of IFC_OBJECT_CLASSES) {
    if (className.length > best.length && withoutPrefix.startsWith(className)) best = className;
  }
  return best;
}

/** The walker's own radius (m): how close a body may come to a wall. */
export const NAV_WALK_RADIUS_M = 0.3;

/**
 * The probe heights over the feet the wall test casts at (m) — "knee/waist
 * height" in the card's own words: two horizontal rays that stand in for the
 * body's column, so a low parapet under a window stops it (a knee ray passes
 * under the sill while the waist ray meets the wall) and a waist-high opening
 * does not (both rays pass).
 */
export const NAV_WALL_KNEE_M = 0.55;
export const NAV_WALL_WAIST_M = 1.0;

/**
 * Whether a wall inside this frame's step refuses it: the nearest hit (of the
 * two probe heights) closer than the step plus the body's own radius. `null` is
 * "this ray met nothing", never "no walls at all".
 */
export function wallBlocksStep(
  hitDistances: readonly (number | null)[],
  travel: number,
  radius: number = NAV_WALK_RADIUS_M,
): boolean {
  if (!Number.isFinite(travel) || travel < 0) return false;
  const reach = travel + (Number.isFinite(radius) ? radius : 0);
  for (const distance of hitDistances) {
    if (distance === null || !Number.isFinite(distance)) continue;
    if (distance <= reach) return true;
  }
  return false;
}

/**
 * The wall's own normal, flattened to the ground plane: a ray may meet a
 * sloped or horizontal face (a roof edge, a reveal), and only the horizontal
 * part can say which way a body is pushed away from the surface.
 */
export function horizontalNormal(normal: NavPoint): NavPoint {
  const flat = { x: normal.x, y: 0, z: normal.z };
  const length = Math.hypot(flat.x, flat.z);
  if (length < 1e-4) return { x: 0, y: 0, z: 0 };
  return { x: flat.x / length, y: 0, z: flat.z / length };
}

/**
 * The step a blocked walker actually takes: the movement direction with the
 * wall's normal removed — the SLIDE along the surface (the card's "forward
 * probe with a slide"). Walking straight into a wall gives back the zero
 * vector, and the caller refuses the step instead of sliding nowhere.
 */
export function slideAlongWall(dir: NavPoint, normal: NavPoint): NavPoint {
  const n = horizontalNormal(normal);
  if (n.x === 0 && n.z === 0) return { x: 0, y: 0, z: 0 };
  const into = dir.x * n.x + dir.z * n.z;
  const slid = { x: dir.x - into * n.x, y: 0, z: dir.z - into * n.z };
  if (Math.hypot(slid.x, slid.z) < 1e-3) return { x: 0, y: 0, z: 0 };
  return normalizeDirection(slid);
}

// ─── The jump (card t_565b83bf): the agent FAB's own key, as a hop ───────────

/**
 * The owner: *"wire the exact same key like in the agent fab window.."* — and
 * the FAB's own keymap ends with Space = Jump (`useOttoDogKeyboard`'s
 * `Jump_Inplace` row), with J as the twin this card names. Here the jump is
 * what a hop has to be in a walker: the body leaves the floor on an arc and
 * lands back on the surface the floor probe resolves — no climbing, no
 * teleporting, and never a fall through the model.
 */
export const NAV_JUMP_KEYS: readonly string[] = [' ', 'j'];

/** Lift-off speed (m/s) and the gravity the arc uses; apex ≈ 0.43 m. */
export const NAV_JUMP_SPEED_MS = 2.9;
export const NAV_GRAVITY_MS2 = 9.81;

export interface NavJump {
  /** Height over the floor the walker stands on (m). */
  offset: number;
  /** Vertical velocity (m/s), positive up. */
  velocity: number;
  active: boolean;
}

/** A jump at lift-off — the state a keydown hands the frame loop. */
export function startJump(speed: number = NAV_JUMP_SPEED_MS): NavJump {
  const lift = Number.isFinite(speed) && speed > 0 ? speed : NAV_JUMP_SPEED_MS;
  return { offset: 0, velocity: lift, active: true };
}

/** One frame of the arc, or the same jump when there is nothing to integrate. */
export function stepJump(jump: NavJump, dt: number): NavJump {
  if (!jump.active) return jump;
  if (!Number.isFinite(dt) || dt <= 0) return jump;
  const velocity = jump.velocity - NAV_GRAVITY_MS2 * dt;
  const offset = jump.offset + velocity * dt;
  if (offset <= 0) return { offset: 0, velocity: 0, active: false };
  return { offset, velocity, active: true };
}

/** The apex of a jump at a given lift-off speed (m) — what a rig asserts. */
export function jumpApexM(speed: number = NAV_JUMP_SPEED_MS): number {
  if (!Number.isFinite(speed) || speed <= 0) return 0;
  return (speed * speed) / (2 * NAV_GRAVITY_MS2);
}

/**
 * The keys that MOVE the character in the first-person modes (card t_e86ce88e).
 *
 * The owner, 2026-10-11, verbatim: *"the float mode arrow controls are not
 * working correctly. arrows should move the character not the view. the mouse
 * should be for looking around."* — so the arrows are movement here, exactly
 * like WASD (which is what every first-person scheme he named — the app's own
 * avatar/people surfaces define none, so the standard game scheme is the one
 * adopted: WASD/arrows translate, the pointer looks, Shift runs).
 *
 * Card t_01ebef40 turned the left/right pair into a STEERING axis in the
 * walking modes (the keys are the same; `navInputForMode` decides what they
 * mean) and gave SIMULATION mode the `V` view switch — see both below. Card t_493c5f58
 * then retired FLOAT and with it the rise/fall pair (Space/Q/E were FLOAT's
 * vertical key and its alias; Space is now only the jump, J its twin, both SIMULATION's
 * own).
 */
export const NAV_MOVE_FORWARD_KEYS = ['w', 'arrowup'] as const;
export const NAV_MOVE_BACK_KEYS = ['s', 'arrowdown'] as const;
export const NAV_MOVE_LEFT_KEYS = ['a', 'arrowleft'] as const;
export const NAV_MOVE_RIGHT_KEYS = ['d', 'arrowright'] as const;
/**
 * The run modifier (card t_565b83bf): the agent FAB's own keymap spells run as
 * Shift (`Sh+↑` in `useOttoDogKeyboard`), and the owner's ask here is *"the
 * exact same key like in the agent fab window"* — Ctrl is the second spelling
 * his card names, so both are the same axis.
 */
export const NAV_BOOST_KEYS = ['shift', 'control'] as const;
export const NAV_BOOST_KEY = 'shift';

/** Every key the first-person reader looks at (lowercase `KeyboardEvent.key`). */
export const NAV_MOVEMENT_KEYS: readonly string[] = [
  ...NAV_MOVE_FORWARD_KEYS,
  ...NAV_MOVE_BACK_KEYS,
  ...NAV_MOVE_LEFT_KEYS,
  ...NAV_MOVE_RIGHT_KEYS,
  ...NAV_BOOST_KEYS,
  ...NAV_JUMP_KEYS,
];

function anyPressed(pressed: (key: string) => boolean, keys: readonly string[]): boolean {
  return keys.some((key) => pressed(key));
}

/**
 * The key that toggles SIMULATION mode's camera view (card t_01ebef40). `C`, owner
 * 2026-10-13: *"make key "C" togle the two.."* — two views is a toggle, and the
 * pill's own chip is the button that does the same thing.
 */
export const NAV_VIEW_TOGGLE_KEY = 'c';

/**
 * One frame of movement input from the keys currently down. Pure, so the
 * scheme itself (which key does what, and that the arrows are MOVEMENT) is
 * pinned by a unit test rather than by reading the scene's event handlers.
 *
 * This is the RAW reading — one axis per key group, no mode involved: the
 * left/right keys land on `strafe` and `turn` stays 0. Which of the two the
 * active mode reads is `navInputForMode`'s decision, and the only place a key's
 * MEANING changes with the mode.
 */
export function navInputFromPressed(pressed: ReadonlySet<string>): NavMoveInput {
  const isDown = (key: string) => pressed.has(key);
  return {
    forward: (anyPressed(isDown, NAV_MOVE_FORWARD_KEYS) ? 1 : 0) - (anyPressed(isDown, NAV_MOVE_BACK_KEYS) ? 1 : 0),
    strafe: (anyPressed(isDown, NAV_MOVE_RIGHT_KEYS) ? 1 : 0) - (anyPressed(isDown, NAV_MOVE_LEFT_KEYS) ? 1 : 0),
    turn: 0,
    boost: anyPressed(isDown, NAV_BOOST_KEYS),
  };
}

/**
 * One frame of movement input, resolved FOR THE ACTIVE MODE (cards t_01ebef40,
 * t_565b83bf and t_493c5f58).
 *
 * The owner, 2026-10-11: *"in gravity mode (and game mode) the arrows should be
 * moving the object, like the car.."* + *"left right should not move object
 * sideways, but turn the ... character's view"* — and on 2026-10-12, after
 * testing the agent FAB's four-way scheme in SIMULATION: *"game mode control should be
 * like gravity (now left and right arrows keys move object sideways) .. the
 * chase view mouse should thurn the object.."*. So the CAR reading is the
 * scheme for BOTH walking modes, and the earlier FAB-identical four-way walk
 * (card t_565b83bf) is superseded: Left/Right (and their A/D twins) STEER —
 * ArrowUp/ArrowDown (and W/S) walk along whatever the facing is.
 */
export function navInputForMode(mode: BimNavMode, pressed: ReadonlySet<string>): NavMoveInput {
  const raw = navInputFromPressed(pressed);
  if (!isWalkerMode(mode)) return raw;
  return { ...raw, strafe: 0, turn: raw.strafe };
}

/**
 * The tallest rise the walker takes in one step. A stair riser is 0.17–0.19 m
 * on this house, so this clears a flight comfortably; anything taller is not a
 * step but a wall/ledge, and v1 walks through those (stated in the card).
 */
export const NAV_STEP_UP_MAX_M = 0.45;

/** Walking speed (m/s), which Shift boosts; one base for both walking modes. */
export const NAV_WALK_SPEED_MS = 2.6;
export const NAV_BOOST_MULTIPLIER = 2.2;

/** Velocity approaches its target at this rate (1/s) — the smooth start/stop. */
export const NAV_VELOCITY_SMOOTHING = 10;

/** Look sensitivity: radians of rotation per pixel of pointer travel. */
export const NAV_LOOK_SENSITIVITY = 0.0022;

/** Pitch limit (~83°), so the walker never flips over at the zenith. */
export const NAV_PITCH_LIMIT = 1.45;

/** The camera's Y chases the resolved floor at this rate (1/s) — no stepping pop. */
export const NAV_EYE_Y_SMOOTHING = 9;

/** Two surface heights closer than this are the same level (m). */
export const NAV_LEVEL_MERGE_TOLERANCE_M = 0.15;

/**
 * The support probe's own budget (card t_9cf7e787, measured): the downward ray
 * runs against the model's walkable surfaces only, and at most once per
 * interval while the walker stays within the skip radius of the last answer.
 * Measured on the demo house before this bound: the cast against every mesh of
 * the 20 MB model took longer than the frame it ran in — walking ran at a third
 * of the idle frame rate. Between probes the feet keep the surface they were
 * last measured on, so a step boundary is crossed at most one interval late.
 */
export const NAV_PROBE_INTERVAL_MS = 80;
export const NAV_PROBE_SKIP_M = 0.25;

// ─── The walker's FOOTPRINT (owner, 2026-10-20) ──────────────────────────────
//
// *"a small crevice in the model e.g. by floor lifted by a millimetre can make
// avatar slip through.. cant we assign some radius to the model so it can't fall
// through?"* — the floor probe used to be ONE ray through the body's centre, and a
// hairline crack between two plates swallowed it. The body now stands on a
// footprint: the centre is asked first, and only when it finds nothing a ring of
// points around it catches the rim that is still floor.

/** How far from the body's centre the floor is still asked (m) — half the wall radius. */
export const NAV_SUPPORT_RADIUS_M = 0.15;

/** The ring's eight directions, so "which sample is the floor" is a pure rule. */
export const NAV_SUPPORT_RING: readonly { x: number; z: number }[] = Object.freeze([
  { x: 1, z: 0 },
  { x: -1, z: 0 },
  { x: 0, z: 1 },
  { x: 0, z: -1 },
  { x: Math.SQRT1_2, z: Math.SQRT1_2 },
  { x: -Math.SQRT1_2, z: Math.SQRT1_2 },
  { x: Math.SQRT1_2, z: -Math.SQRT1_2 },
  { x: -Math.SQRT1_2, z: -Math.SQRT1_2 },
]);

/**
 * The support the body stands on: the CENTRE's answer when there is one (that is
 * the floor it is walking on), else the highest ring sample that is no more than a
 * step above the feet — a chair seat under the footprint is an obstacle, not the
 * ground, while the rim of a hairline crack is. `null` when nothing in the
 * footprint carries the body: a stair void wider than the ring, and it falls.
 */
export function pickFootprintSupport(
  centreY: number | null,
  ringYs: readonly (number | null)[],
  feetY: number,
  stepUp: number = NAV_STEP_UP_MAX_M,
): number | null {
  if (centreY !== null && Number.isFinite(centreY)) return centreY;
  let best: number | null = null;
  for (const y of ringYs) {
    if (y === null || !Number.isFinite(y)) continue;
    if (y > feetY + stepUp) continue;
    if (best === null || y > best) best = y;
  }
  return best;
}

export interface NavPoint {
  x: number;
  y: number;
  z: number;
}

/** A camera orientation in the viewer's own convention (three's YXZ Euler). */
export interface NavLook {
  yaw: number;
  pitch: number;
}

/** One frame of movement input: -1/0/1 per axis plus the boost modifier. */
export interface NavMoveInput {
  forward: number;
  /**
   * The lateral axis of the RAW key reading. Both first-person modes are walkers
   * now, so in practice a mode resolved reading carries this at 0 and the keys'
   * lateral sense arrives on `turn` instead (card t_493c5f58); the field stays
   * because it is what the raw reader produces and what a future mode may read.
   */
  strafe: number;
  /**
   * The steering axis of the walking modes (card t_01ebef40). +1 is "turn
   * right" — the sense the strafe axis had, so the same key that drifted right
   * in the retired FLOAT mode turns right on foot.
   */
  turn: number;
  boost: boolean;
}

export function clampPitch(pitch: number, limit: number = NAV_PITCH_LIMIT): number {
  if (!Number.isFinite(pitch)) return 0;
  return Math.max(-limit, Math.min(limit, pitch));
}

/**
 * The look angles that reproduce a world direction. Inverse of
 * `directionFromLook`: three's default camera looks down −Z, so a direction
 * (dx, dy, dz) reads yaw = atan2(−dx, −dz), pitch = asin(dy).
 */
export function lookFromDirection(dir: NavPoint): NavLook {
  const length = Math.hypot(dir.x, dir.y, dir.z) || 1;
  const x = dir.x / length;
  const y = dir.y / length;
  const z = dir.z / length;
  return { yaw: Math.atan2(-x, -z), pitch: clampPitch(Math.asin(Math.max(-1, Math.min(1, y)))) };
}

/**
 * The look with its head LEVELED to the horizon — the same BEARING a world
 * direction approaches on, with the pitch dropped (owner, 2026-10-19: *"when
 * dropped into gravity we want to level the 'head' to the horizon.. so we only
 * take azimuth into account"*). Only the azimuth survives, so a room entered from
 * an aerial orbit is entered looking straight ahead across it, not down at its
 * floor. A direction with no horizontal share (straight up or straight down) has
 * no azimuth to keep, and the caller's own yaw stands.
 */
export function levelLook(dir: NavPoint, fallbackYaw = 0): NavLook {
  const horizontal = Math.hypot(dir.x, dir.z);
  if (!(horizontal > 1e-6)) return { yaw: fallbackYaw, pitch: 0 };
  return { yaw: Math.atan2(-dir.x / horizontal, -dir.z / horizontal), pitch: 0 };
}

/** The unit direction a look angle points at (three's YXZ Euler). */
export function directionFromLook(look: NavLook): NavPoint {
  const cp = Math.cos(look.pitch);
  return {
    x: -cp * Math.sin(look.yaw),
    y: Math.sin(look.pitch),
    z: -cp * Math.cos(look.yaw),
  };
}

/** How far outside the model's own extent a walk's default spawn stands (metres). */
export const WALK_SPAWN_MARGIN_M = 1.5;

/**
 * Where a WALK starts when it has no remembered place of its own (owner,
 * 2026-10-14: *"gravity should have a default position set on the model"*).
 *
 * The old rule was "from wherever the camera is — no jump, no teleport", which is
 * fine from a normal orbit and stranded you when it was not: orbit out to look at
 * the whole house, click Gravity, and the walk began in mid-air far from anything
 * with no floor under it. A walk has a PLACE, so this computes one from the model
 * itself:
 *
 *  · OUTSIDE the model's own extent plus `WALK_SPAWN_MARGIN_M` — inside would be
 *    inside a wall, and the margin is what keeps the spawn clear of the façade;
 *  · on the side you were already looking from, so the house is in front of you
 *    rather than behind, and the switch is not disorienting;
 *  · with the feet on `floorY` (the ground floor), a body's own height;
 *  · facing the model's centre, pitched to the model's mid height rather than
 *    level, so the house is what the view is made of.
 *
 * A camera directly over the centre has no side to come from, so the spawn is due
 * +Z: any answer beats standing in the middle of the geometry.
 */
export function defaultWalkPose(input: {
  /** The model's own centre. */
  center: NavPoint;
  /** Half the model's horizontal extent — how far out the façade reaches. */
  radiusXZ: number;
  /** The floor the feet stand on. */
  floorY: number;
  /** Where the camera is now: which side the walk should approach from. */
  from: NavPoint;
  eyeHeightM?: number;
}): NavPoint & NavLook {
  const eye = input.eyeHeightM ?? NAV_EYE_HEIGHT_M;
  const dx = input.from.x - input.center.x;
  const dz = input.from.z - input.center.z;
  const length = Math.hypot(dx, dz);
  const outward = length > 0.01 ? { x: dx / length, z: dz / length } : { x: 0, z: 1 };
  const distance = Math.max(input.radiusXZ, 0) + WALK_SPAWN_MARGIN_M;
  const x = input.center.x + outward.x * distance;
  const y = input.floorY;
  const z = input.center.z + outward.z * distance;
  const look = lookFromDirection({
    x: input.center.x - x,
    y: input.center.y - (y + eye),
    z: input.center.z - z,
  });
  return { x, y, z, yaw: look.yaw, pitch: look.pitch };
}

/**
 * Apply a pointer delta (px) to a look angle.
 *
 * THE DRAG CONVENTION (card t_493c5f58): a drag moves the WORLD, not the eye —
 * you take hold of the view and pull it. Drag up and the world comes up, so the
 * head ends up looking DOWN; drag right and the world goes right, so the head
 * ends up looking LEFT. Both axes are the same rule, which is the point: the
 * owner, 2026-10-12, on the build that had the FPS convention on one axis and
 * this one on the other — *"the mouse drage to turn head should be inverted..
 * upd and down movement is good. but left nad right is off"* — i.e. keep the
 * vertical exactly as it is and flip the horizontal to match it.
 *
 * The vertical sign is therefore untouched (`+dy`), and the horizontal is the
 * one that moved: it shipped as `look.yaw - dxPx * sensitivity` ("drag right
 * looks right", the FPS reading) and is now `+dxPx`.
 *
 * The car keys are NOT this convention: `applyTurn` keeps "right means right",
 * because the owner asked for the keys to turn the character and never asked for
 * the steering wheel to be reversed — a hand on the keys steers, a hand on the
 * model drags it. One view drags the other way round by name — SIMULATION's drone
 * (`droneDragDeltas`, owner, 2026-10-15).
 */
export function applyLookDelta(
  look: NavLook,
  dxPx: number,
  dyPx: number,
  sensitivity: number = NAV_LOOK_SENSITIVITY,
): NavLook {
  return {
    yaw: look.yaw + dxPx * sensitivity,
    pitch: clampPitch(look.pitch + dyPx * sensitivity),
  };
}

/**
 * How fast the character yaws while a steering key is held, in radians per
 * second (card t_01ebef40): 1.8 rad/s ≈ 103°/s — a little over a right angle
 * per second, which turns the walker around comfortably without the "spinning
 * on the spot" a faster rate gives.
 */
export const NAV_TURN_RATE_RAD_S = 1.8;

/**
 * Apply one frame of steering to the look (card t_01ebef40). Turning RIGHT (+1,
 * the sense the raw lateral axis carries) turns the view right, i.e. DECREASES
 * yaw.
 *
 * This is deliberately NOT the drag's own sign (card t_493c5f58 flipped the
 * drag; see `applyLookDelta`): a steering key is a direction the user names —
 * press Right and the character turns right — while a drag is the world being
 * pulled, so the two conventions are allowed to differ, and each was named by
 * the owner separately. What they keep in common is the axis: yaw, and the pitch
 * untouched either way.
 *
 * Steering is a steering wheel, not a throttle: `boost` does not change it, and
 * a turn applies while standing still, which is how a stationary character
 * re-aims before walking on. The pitch is untouched — the keys never look up or
 * down.
 */
export function applyTurn(look: NavLook, turn: number, dt: number, rate: number = NAV_TURN_RATE_RAD_S): NavLook {
  if (!Number.isFinite(turn) || turn === 0) return look;
  if (!Number.isFinite(dt) || dt <= 0 || !Number.isFinite(rate)) return look;
  return { yaw: look.yaw - turn * rate * dt, pitch: look.pitch };
}

/**
 * The walking direction: the look's heading flattened to the ground plane, so
 * looking down at your feet does not walk you into the floor, plus the strafe
 * on the flat right vector (yaw + 90°).
 */
export function walkDirection(look: NavLook, input: NavMoveInput): NavPoint {
  const heading = { x: -Math.sin(look.yaw), z: -Math.cos(look.yaw) };
  // right = forward × up, on the plane: (−z, 0, x) of the heading.
  const right = { x: -heading.z, z: heading.x };
  return {
    x: heading.x * input.forward + right.x * input.strafe,
    y: 0,
    z: heading.z * input.forward + right.z * input.strafe,
  };
}

/** Normalise a direction, or return the zero vector when there is nothing to do. */
export function normalizeDirection(dir: NavPoint): NavPoint {
  const length = Math.hypot(dir.x, dir.y, dir.z);
  if (length < 1e-6) return { x: 0, y: 0, z: 0 };
  return { x: dir.x / length, y: dir.y / length, z: dir.z / length };
}

/** The speed a walker moves at, with the Shift boost applied. */
export function navSpeed(boost: boolean): number {
  return boost ? NAV_WALK_SPEED_MS * NAV_BOOST_MULTIPLIER : NAV_WALK_SPEED_MS;
}

/** Exponential approach — the frame-rate independent damping of every axis. */
export function approach(current: number, target: number, rate: number, dt: number): number {
  if (!Number.isFinite(dt) || dt <= 0) return current;
  const k = 1 - Math.exp(-rate * Math.max(0, dt));
  return current + (target - current) * k;
}

export function approachPoint(current: NavPoint, target: NavPoint, rate: number, dt: number): NavPoint {
  return {
    x: approach(current.x, target.x, rate, dt),
    y: approach(current.y, target.y, rate, dt),
    z: approach(current.z, target.z, rate, dt),
  };
}

/** Drop a velocity that has decayed to nothing, so the render loop can idle. */
export function isStill(velocity: NavPoint, epsilon = 1e-3): boolean {
  return Math.hypot(velocity.x, velocity.y, velocity.z) < epsilon;
}

/**
 * The element types whose top surface is a floor the walker may stand on: the
 * model's own room finishes (`Living Room Floor Finish`, an `IfcCovering`).
 * They are what the demo house walks on — its `IfcSlab` elements are the roof
 * and terrace structures, not the floors — so the fallback level list is built
 * from these plus the storey elevations, and the live surface comes from the
 * geometry itself.
 */
export function isFloorFinishType(typeName: string): boolean {
  const upper = typeName.toUpperCase();
  return upper === 'COVERING' || upper === 'FLOOR' || upper === 'FLOORING';
}

/**
 * Whether a mesh may carry the walker (card t_9cf7e787): the room floor
 * finishes, plus the stair structure and the slabs (this house's three IfcSlabs
 * are the roof and terrace decks, so the terrace is walkable too). The support
 * probe is restricted to these, which is what keeps a walking frame cheap — the
 * same question answered against a handful of horizontal surfaces instead of
 * every wall, window and lamp in the model.
 */
export function isWalkSurfaceType(typeName: string): boolean {
  if (isFloorFinishType(typeName)) return true;
  return matchesWalkToken(typeName);
}

/**
 * …and the same rule again for the element's own NAME, because this house's
 * staircase is not an `IfcStair` in the served model — its flight is 14
 * `IfcBuildingElementProxy` instances named `TREAD-…` plus the landing, and a
 * type-only rule would leave the walker with no surface to climb.
 */
export function isWalkSurfaceName(name: string): boolean {
  return matchesWalkToken(name);
}

/**
 * The walker's THIRD source of surfaces (card t_e86ce88e): geometry, for the
 * surfaces no type or name token ever names — this house's terrain plates are
 * plain `IfcBuildingElementProxy` blocks named `TR - 015`/`TR - 016`, and the
 * owner's report (*"in gravity mode.. gravity mode does not pin you down to the
 * nearest surface"*) is exactly the walker stepping off a deck with nothing but
 * terrain beneath it: the probe missed, and the storey level it fell back to
 * left it hanging in mid-air.
 *
 * The rule is a box, not a word list: a surface you can stand on is THIN next to
 * its own footprint (a wall is not, a floor plate always is) and wide enough for
 * two feet. Every judgement is in metres, and a box that is not finite is not a
 * surface.
 */
export const NAV_PLATE_MIN_SPAN_M = 1.5;
export const NAV_PLATE_HEIGHT_RATIO = 0.5;
export const NAV_PLATE_HEIGHT_SLACK_M = 0.6;

export function isPlateLikeSurfaceBox(box: {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
}): boolean {
  const spanX = box.max.x - box.min.x;
  const spanY = box.max.y - box.min.y;
  const spanZ = box.max.z - box.min.z;
  if (![spanX, spanY, spanZ].every((span) => Number.isFinite(span) && span >= 0)) return false;
  const longest = Math.max(spanX, spanZ);
  if (longest < NAV_PLATE_MIN_SPAN_M) return false;
  const footprint = Math.min(spanX, spanZ);
  return spanY <= Math.max(NAV_PLATE_HEIGHT_SLACK_M, NAV_PLATE_HEIGHT_RATIO * footprint);
}

/**
 * WATER IS NOT A FLOOR (owner, 2026-10-21: *"we want the water to be real in our final glb model…
 * would be good if our walk and simulation modes would allow to sink in"*).
 *
 * A pool's surface is a horizontal plate exactly like a terrace deck, so every rule above
 * happily makes it walkable: the plate rule sees a thin wide box, the support ray lands on it,
 * and the walker strolls across the water. The water surface is therefore excluded from the
 * support probe by IDENTITY, not by shape — the loaded model hands the viewer the meshes it
 * imported, and the ones that are water are skipped, so the ray carries on down to the basin
 * floor underneath. That single exclusion is what lets a body walk off the coping and sink:
 * the probe finds the pool floor 1.25 m below, and `resolveWalkFeetY` follows any DROP (only
 * climbs are limited by `NAV_STEP_UP_MAX_M`).
 *
 * The hints are all the formats spell it in: our own GLB exporter sets `extras.water`, the IFC
 * path leaves the element named *Pool Water*, and a material may be named `Voda (pool water)`.
 * Word-bounded so `Watering` or a `WaterTank` device does not match.
 */
export const WATER_SURFACE_NAME_RE = /(^|[^a-z])(water|voda|pool[\s_-]*water)([^a-z]|$)/i;

export interface WaterSurfaceHints {
  /** The mesh/node name, or the element's own IFC name. */
  name?: string | null;
  /** The material's name (`Voda (pool water)` from this repo's exporter). */
  materialName?: string | null;
  /** …and the explicit flag a glTF can carry (`extras.water`). */
  flag?: boolean;
}

/** Is this mesh the WATER SURFACE — i.e. something a body sinks THROUGH, never stands on? */
export function isWaterSurface(hints: WaterSurfaceHints): boolean {
  if (hints.flag === true) return true;
  for (const value of [hints.name, hints.materialName]) {
    if (value && WATER_SURFACE_NAME_RE.test(value)) return true;
  }
  return false;
}

function matchesWalkToken(value: string): boolean {
  const upper = value.toUpperCase();
  return ['STAIR', 'STEP', 'RAMP', 'LANDING', 'SLAB', 'TREAD'].some((token) => upper.includes(token));
}

/** Merge surface heights into one sorted level list, deduplicating near-equal ones. */
export function mergeFloorLevels(levels: readonly number[], tolerance: number = NAV_LEVEL_MERGE_TOLERANCE_M): number[] {
  const finite = levels.filter((level) => Number.isFinite(level)).sort((a, b) => a - b);
  const merged: number[] = [];
  for (const level of finite) {
    const last = merged[merged.length - 1];
    if (last === undefined || Math.abs(level - last) > tolerance) merged.push(level);
  }
  return merged;
}

/**
 * One storey's own elevation, in metres.
 *
 * A model may state `IfcBuildingStorey.Elevation` in metres or — as the demo
 * house's export does — in millimetres (`Ground` 0, `First` 3050, `Roof` 7660),
 * whatever its unit assignment says. The loaded geometry is the referee: a
 * value that sits far outside the model's own vertical extent is the same
 * number in a smaller unit if THAT one lands inside. A value neither spelling
 * can place (the site level 6 km below the house) is not a floor and is
 * dropped, never clamped into one.
 */
export function normalizeStoreyElevation(
  elevation: number,
  boundsY?: { min: number; max: number } | null,
  marginM: number = 3,
): number | null {
  if (!Number.isFinite(elevation)) return null;
  if (!boundsY || !Number.isFinite(boundsY.min) || !Number.isFinite(boundsY.max)) return elevation;
  const fits = (value: number) => value >= boundsY.min - marginM && value <= boundsY.max + marginM;
  if (fits(elevation)) return elevation;
  const asMillimetres = elevation / 1000;
  if (fits(asMillimetres)) return asMillimetres;
  return null;
}

/** Build the fallback level list from the model's own storeys and floor finishes. */
export function floorLevelsFromSurfaces(
  surfaces: {
    storeyElevations?: readonly number[];
    floorTops?: readonly number[];
    /** The model's vertical extent, in the same metres as `floorTops`. */
    boundsY?: { min: number; max: number } | null;
  },
  tolerance: number = NAV_LEVEL_MERGE_TOLERANCE_M,
): number[] {
  const storeys = (surfaces.storeyElevations ?? [])
    .map((elevation) => normalizeStoreyElevation(elevation, surfaces.boundsY))
    .filter((elevation): elevation is number => elevation !== null);
  return mergeFloorLevels([...storeys, ...(surfaces.floorTops ?? [])], tolerance);
}

/** The highest level at or below `y` (null when every level is above it). */
export function nearestLevelAtOrBelow(levels: readonly number[], y: number): number | null {
  let best: number | null = null;
  for (const level of levels) {
    if (level <= y && (best === null || level > best)) best = level;
  }
  return best;
}

export interface WalkResolveInput {
  /** The walking surface underfoot, measured by the scene (null = nothing hit). */
  supportY: number | null;
  /** The fallback levels derived from the model. */
  levels: readonly number[];
  /** Where the feet are now. */
  feetY: number;
  /** The tallest rise a single step may take. */
  stepUpMax?: number;
}

export interface WalkResolveResult {
  /** Where the feet go this frame. */
  feetY: number;
  /** True when the surface ahead is more than one step up: the move is refused. */
  blocked: boolean;
}

/**
 * Resolve the walker's feet for this frame from the surface underfoot.
 *
 * A surface at or below the step-up ceiling is taken as-is — a descent (walking
 * off the gallery) is allowed and smoothed by the camera's own Y chase. Nothing
 * underfoot falls back to the model's level list, and a level above the step-up
 * ceiling (a platform, a wall top) refuses the move instead of climbing it.
 * Walls are not tested at all: v1 walks through them, as the card states.
 *
 * Nothing to fall back to either — under every level the model knows — is refused
 * rather than invented: a walker that far under the model is not standing
 * anywhere the walk can reason about.
 */
export function resolveWalkFeetY(input: WalkResolveInput): WalkResolveResult {
  const stepUpMax = input.stepUpMax ?? NAV_STEP_UP_MAX_M;
  const { supportY, levels, feetY } = input;

  if (supportY !== null && Number.isFinite(supportY)) {
    if (supportY - feetY > stepUpMax) return { feetY, blocked: true };
    return { feetY: supportY, blocked: false };
  }

  const fallback = nearestLevelAtOrBelow(levels, feetY + stepUpMax);
  if (fallback === null) return { feetY, blocked: true };
  return { feetY: fallback, blocked: false };
}

/** The camera's Y while walking: the feet plus the eye height. */
export function walkCameraY(feetY: number, eyeHeight: number = NAV_EYE_HEIGHT_M): number {
  return feetY + eyeHeight;
}

// ─── The section cut vs the walker (owner, 2026-10-18) ───────────────────────

/**
 * The section cut is a viewing BAND: the model is drawn only between its floor
 * and its ceiling plane, and everything outside is removed. The walker stands on
 * the authored building, so the band and the body have one rule —

 *   · the COLLISION never consults the band (`probeWalkSupport`,
 *     `probeWallAhead`): a cut wall still stops you and a cut floor still carries
 *     you. The owner's report, verbatim: *"you can fall into a cut wall.. when
 *     the model is vertiallly cut.. you fall into an cant get out?"* — a probe
 *     that skipped clipped hits deleted the wall he was leaning on (the stub
 *     under the band then refused every direction, so he was stuck inside a
 *     half-wall) and the floor under his feet when the band's floor rose above
 *     it (no support and no storey fallback: frozen);
 *   · the BAND never takes the ground out from under him: while a walking mode
 *     is active the band is widened — never shrunk — until it contains his feet.
 *     A band that isolates another storey is still a fine view in orbit, and the
 *     walk hands the operator's own band back when it ends.
 */
export interface WalkCutBand {
  /** The band's floor plane (the cut's `clipBottomY`). */
  bottom: number;
  /** The band's ceiling plane (the cut's `clipHeightY`). */
  top: number;
}

/** How far below the feet the band's floor is pulled, so the floor stays drawn (m). */
export const WALK_CUT_FEET_MARGIN_M = 0.02;
/** How far above the feet the band's ceiling is pushed when a band missed him (m). */
export const WALK_CUT_FEET_SPAN_M = 0.1;
/** …and how far above the EYES while walking: the wall he is facing must stay drawn (m). */
export const WALK_CUT_HEAD_MARGIN_M = 0.2;

/**
 * The band, widened until it contains the walker — the same band when it already
 * does (so the caller can compare and skip a write). Only the two edges the walker
 * is outside of move, and they move outward: a storey slice above him keeps its
 * ceiling and gains his feet, a slice below him keeps its floor and gains them too.
 *
 * `aboveFeetM` is how much of the body the band must hold above the feet: the
 * default (`WALK_CUT_FEET_SPAN_M`) is the walk's own "is he inside it at all" test,
 * and the scene passes the eye height instead — a band whose ceiling sits BELOW the
 * eyes paints a wall away that still blocks the walk, which reads as an invisible
 * wall (owner, 2026-10-18: *"you can fall into a cut wall"*).
 */
export function walkCutBand(
  band: WalkCutBand,
  feetY: number,
  aboveFeetM: number = WALK_CUT_FEET_SPAN_M,
): WalkCutBand {
  if (!Number.isFinite(feetY)) return band;
  const span = Number.isFinite(aboveFeetM) ? aboveFeetM : WALK_CUT_FEET_SPAN_M;
  const bottom = Number.isFinite(band.bottom) ? Math.min(band.bottom, feetY - WALK_CUT_FEET_MARGIN_M) : feetY - WALK_CUT_FEET_MARGIN_M;
  const top = Number.isFinite(band.top) ? Math.max(band.top, feetY + span) : feetY + span;
  if (bottom === band.bottom && top === band.top) return band;
  return { bottom, top };
}
